import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  InputAdornment,
  Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { translations } from '../translations/productSelectorTranslation';
import {
  ZERO,
  ONE,
  RADIX_DECIMAL,
  FIRST_ELEMENT_INDEX,
  DIALOG_HEIGHT_VH,
  TABLE_MAX_HEIGHT_PX,
  TABLE_MIN_HEIGHT_PX,
  MIN_WIDTH_SERIAL_SELECT,
  MIN_WIDTH_QUANTITY_CELL,
  WIDTH_QUANTITY_INPUT_XS,
  WIDTH_QUANTITY_INPUT_SM,
  SPIN_BUTTON_HEIGHT,
  COLOR_ERROR_BACKGROUND,
  COLOR_WARNING_BACKGROUND,
  TABLE_COLSPAN_WITH_SERIAL,
  TABLE_COLSPAN_WITHOUT_SERIAL,
  INPUT_TYPE_NUMBER,
  INPUT_SIZE_SMALL,
  VARIANT_OUTLINED,
  DIALOG_MAX_WIDTH,
  PRODUCT_FIELDS,
  EMPTY_STRING,
  UI_TEXT,
  LIVE_DATA_SPINNER_SIZE
} from '../constants/productSelectorConstants';
import { BRAND, BRAND_DARK, BRAND_LIGHT_BG } from '../constants/salesPromotionsTheme';

const SKELETON_ROW_COUNT = 8;

const getBalance = (product) =>
  product[PRODUCT_FIELDS.LOG_COUNTERS_SUBFORM]?.[FIRST_ELEMENT_INDEX]?.[PRODUCT_FIELDS.BALANCE];

const ProductSelector = ({ products, onProductsChange, open, onClose, isLoadingProducts, onFetchLiveData, productsLoadProgress }) => {
  const [localSelectedProducts, setLocalSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState(EMPTY_STRING);
  const [partNumberSearchTerm, setPartNumberSearchTerm] = useState(EMPTY_STRING);
  const [promoProductSearchTerm, setPromoProductSearchTerm] = useState(EMPTY_STRING);
  const [loadingLiveDataParts, setLoadingLiveDataParts] = useState(() => new Set());

  // Typing in the search fields updates immediately (searchTerm/partNumberSearchTerm -
  // the field itself always shows what was typed). The actual filtering (which
  // re-renders the table, up to ~1000 rows) runs on a time-debounced value (300ms) -
  // meaning during continuous typing the table doesn't update at all, only once
  // after typing stops. This is simpler than useDeferredValue and more effective
  // here too: instead of just "deprioritizing" that same heavy render (which still
  // runs on every keystroke), it simply doesn't run at all until typing pauses.
  const DEBOUNCE_MS = 300;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(EMPTY_STRING);
  const [debouncedPartNumberSearchTerm, setDebouncedPartNumberSearchTerm] = useState(EMPTY_STRING);
  const [debouncedPromoProductSearchTerm, setDebouncedPromoProductSearchTerm] = useState(EMPTY_STRING);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setDebouncedPartNumberSearchTerm(partNumberSearchTerm);
      setDebouncedPromoProductSearchTerm(promoProductSearchTerm);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, partNumberSearchTerm, promoProductSearchTerm]);

  // O(1) lookup instead of localSelectedProducts.find() inside the row loop
  // (which was O(n) per row = O(n^2) per render for a ~900-product table)
  const localSelectedByPart = useMemo(
    () => new Map(localSelectedProducts.map((p) => [p[PRODUCT_FIELDS.PART_NAME], p])),
    [localSelectedProducts]
  );

  const filteredProducts = products.filter((product) =>
    product[PRODUCT_FIELDS.PART_DESCRIPTION].toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
    product[PRODUCT_FIELDS.PART_NAME].toLowerCase().includes(debouncedPartNumberSearchTerm.toLowerCase()) &&
    (debouncedPromoProductSearchTerm === EMPTY_STRING ||
      (debouncedPromoProductSearchTerm === translations.yes && product[PRODUCT_FIELDS.PROMO_EXAMPLE] !== null) ||
      (debouncedPromoProductSearchTerm === translations.no && product[PRODUCT_FIELDS.PROMO_EXAMPLE] === null))
  );

  useEffect(() => {
    // merge (not replace) so an in-flight live-data fetch for one row never
    // wipes out a quantity the user is mid-typing on another row.
    // prevLocal is turned into a Map once (O(n)) instead of calling .find()
    // per product (O(n) inside O(n) = O(n^2)) - with ~900 products loaded
    // that quadratic blowup alone was enough to freeze the tab for seconds
    // every time this effect re-ran (i.e. every time a new page arrived).
    if (open) {
      setLocalSelectedProducts((prevLocal) => {
        const prevByPart = new Map(prevLocal.map((p) => [p[PRODUCT_FIELDS.PART_NAME], p]));
        return products.map((product) => {
          const existing = prevByPart.get(product[PRODUCT_FIELDS.PART_NAME]);
          const quantity = existing?.[PRODUCT_FIELDS.QUANTITY] ?? product[PRODUCT_FIELDS.QUANTITY] ?? ZERO;
          return {
            ...product,
            [PRODUCT_FIELDS.QUANTITY]: quantity,
            [PRODUCT_FIELDS.SELECTED_SERIAL]: existing?.[PRODUCT_FIELDS.SELECTED_SERIAL],
            [PRODUCT_FIELDS.IS_OVER_LIMIT]: quantity > ZERO ? quantity > getBalance(product) : false,
          };
        });
      });
    }
  }, [open, products]);

  const requestLiveData = (partName) => {
    if (!onFetchLiveData || loadingLiveDataParts.has(partName)) return;
    setLoadingLiveDataParts((prev) => new Set(prev).add(partName));
    Promise.resolve(onFetchLiveData([partName])).finally(() => {
      setLoadingLiveDataParts((prev) => {
        const next = new Set(prev);
        next.delete(partName);
        return next;
      });
    });
  };

  const handleQuantityChange = (partName, quantity, balance) => {
    setLocalSelectedProducts(prevProducts =>
      prevProducts.map(product => {
        if (product[PRODUCT_FIELDS.PART_NAME] === partName) {
          return {
            ...product,
            [PRODUCT_FIELDS.QUANTITY]: Math.max(ZERO, quantity),
            [PRODUCT_FIELDS.IS_OVER_LIMIT]: balance !== undefined && quantity > balance
          };
        }
        return product;
      })
    );
  };

  const handleSerialChange = (partName, serialValue) => {
    setLocalSelectedProducts(prevProducts =>
      prevProducts.map(product => {
        if (product[PRODUCT_FIELDS.PART_NAME] === partName) {
          return { ...product, [PRODUCT_FIELDS.SELECTED_SERIAL]: serialValue };
        }
        return product;
      })
    );
  };

  const handleConfirm = () => {
    onProductsChange(localSelectedProducts.filter(product => product[PRODUCT_FIELDS.QUANTITY] > ZERO));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={DIALOG_MAX_WIDTH} fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', maxHeight: DIALOG_HEIGHT_VH, padding: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, p: 1.5, bgcolor: '#fafbfc', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3 }}>
          <TextField
            label={translations.productSearch}
            variant={VARIANT_OUTLINED}
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: '#fff', borderRadius: 1 }}
          />
          <TextField
            label={translations.partNumberSearch}
            variant={VARIANT_OUTLINED}
            fullWidth
            value={partNumberSearchTerm}
            onChange={(e) => setPartNumberSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: '#fff', borderRadius: 1 }}
          />
          <FormControl fullWidth variant={VARIANT_OUTLINED} sx={{ bgcolor: '#fff', borderRadius: 1 }}>
            <InputLabel id="promo-product-label">{translations.promoProduct}</InputLabel>
            <Select
              labelId="promo-product-label"
              value={promoProductSearchTerm}
              onChange={(e) => setPromoProductSearchTerm(e.target.value)}
              label={translations.promoProduct}
            >
              <MenuItem value={EMPTY_STRING}>{translations.all}</MenuItem>
              <MenuItem value={translations.yes}>{translations.yes}</MenuItem>
              <MenuItem value={translations.no}>{translations.no}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {productsLoadProgress && productsLoadProgress.loaded < productsLoadProgress.total && (
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: BRAND_LIGHT_BG,
              border: `1px solid ${BRAND}33`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <DownloadingIcon sx={{ color: BRAND }} fontSize="small" />
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.round((productsLoadProgress.loaded / productsLoadProgress.total) * 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#fff',
                  '& .MuiLinearProgress-bar': { bgcolor: BRAND, borderRadius: 3 },
                }}
              />
              <Typography variant="caption" sx={{ color: BRAND_DARK, fontWeight: 500 }}>
                {`טוען מוצרים נוספים - ${productsLoadProgress.loaded} מתוך ${productsLoadProgress.total}`}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`${Math.round((productsLoadProgress.loaded / productsLoadProgress.total) * 100)}%`}
              sx={{ bgcolor: BRAND, color: '#fff', fontWeight: 700 }}
            />
          </Box>
        )}

        <Box sx={{ height: TABLE_MAX_HEIGHT_PX, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isLoadingProducts ? (
            <TableContainer
              component={Paper}
              sx={{
                flexGrow: 1,
                minHeight: TABLE_MIN_HEIGHT_PX,
                maxHeight: 'calc(100% - 80px)',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        background: 'linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)',
                        fontWeight: 700,
                        color: '#374151',
                        borderBottom: `2px solid ${BRAND}22`,
                      },
                    }}
                  >
                    <TableCell>{translations.product}</TableCell>
                    <TableCell>{translations.partNumber}</TableCell>
                    <TableCell>{translations.inventory}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.promoProduct}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.unitname}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.conv}</TableCell>
                    <TableCell sx={{ minWidth: MIN_WIDTH_QUANTITY_CELL, textAlign: 'center' }}>{translations.quantity}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={50} height={22} /></TableCell>
                      <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={70} height={32} sx={{ mx: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                flexGrow: 1,
                minHeight: TABLE_MIN_HEIGHT_PX,
                maxHeight: 'calc(100% - 80px)',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& .MuiTableCell-head': {
                        background: 'linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)',
                        fontWeight: 700,
                        color: '#374151',
                        borderBottom: `2px solid ${BRAND}22`,
                      },
                    }}
                  >
                    <TableCell>{translations.product}</TableCell>
                    <TableCell>{translations.partNumber}</TableCell>
                    <TableCell>{translations.inventory}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.promoProduct}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.unitname}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{translations.conv}</TableCell>
                    <TableCell sx={{ minWidth: MIN_WIDTH_QUANTITY_CELL, textAlign: 'center' }}>{translations.quantity}</TableCell>
                    {localSelectedProducts && localSelectedProducts.some(p => p[PRODUCT_FIELDS.QUANTITY] > ZERO) && (
                      <TableCell sx={{ minWidth: MIN_WIDTH_QUANTITY_CELL, textAlign: 'center' }}>{translations.SERIAL}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts && filteredProducts.length > ZERO ? (
                    filteredProducts.map((product) => {
                      const selectedProduct = localSelectedByPart.get(product[PRODUCT_FIELDS.PART_NAME]);
                      const productBalance = getBalance(product);
                      const partName = product[PRODUCT_FIELDS.PART_NAME];
                      const isLiveDataLoaded = product[PRODUCT_FIELDS.LIVE_DATA_LOADED];
                      const isLiveDataLoading = loadingLiveDataParts.has(partName);
                      return (
                        <TableRow key={product[PRODUCT_FIELDS.PART_NAME]}
                          hover
                          sx={{
                            backgroundColor: selectedProduct?.[PRODUCT_FIELDS.QUANTITY]
                              ? selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance
                                ? COLOR_ERROR_BACKGROUND
                                : COLOR_WARNING_BACKGROUND
                              : 'inherit'
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {product[PRODUCT_FIELDS.PART_DESCRIPTION]}
                          </TableCell>
                          <TableCell>{partName}</TableCell>
                          <TableCell>
                            {isLiveDataLoaded ? (
                              <Chip
                                size="small"
                                icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
                                label={productBalance}
                                sx={{
                                  bgcolor: productBalance > ZERO ? '#e8f5e9' : '#fdecea',
                                  color: productBalance > ZERO ? '#217346' : '#c62828',
                                  fontWeight: 700,
                                }}
                              />
                            ) : isLiveDataLoading ? (
                              <CircularProgress size={LIVE_DATA_SPINNER_SIZE} sx={{ color: BRAND }} />
                            ) : (
                              <Chip
                                size="small"
                                clickable
                                onClick={() => requestLiveData(partName)}
                                label={UI_TEXT.CHECK_STOCK}
                                sx={{
                                  bgcolor: 'transparent',
                                  border: `1px solid ${BRAND}`,
                                  color: BRAND,
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: `${BRAND}14` },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {product[PRODUCT_FIELDS.PROMO_EXAMPLE] == null ? (
                              <Typography variant="body2" color="text.secondary">{translations.no}</Typography>
                            ) : (
                              <Chip size="small" label={translations.yes} sx={{ bgcolor: BRAND_LIGHT_BG, color: BRAND_DARK, fontWeight: 600 }} />
                            )}
                          </TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.UNIT_NAME]}</TableCell>
                          <TableCell>{product[PRODUCT_FIELDS.CONVERSION]}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField
                                type={INPUT_TYPE_NUMBER}
                                size={INPUT_SIZE_SMALL}
                                value={selectedProduct ? selectedProduct[PRODUCT_FIELDS.QUANTITY] : ZERO}
                                onFocus={() => !isLiveDataLoaded && requestLiveData(partName)}
                                onChange={(e) => {
                                  if (!isLiveDataLoaded) requestLiveData(partName);
                                  handleQuantityChange(partName, parseInt(e.target.value, RADIX_DECIMAL) || ZERO, productBalance);
                                }}
                                InputProps={{
                                  inputProps: {
                                    min: ZERO,
                                    style: { textAlign: 'center' }
                                  }
                                }}
                                error={selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance}
                                helperText={selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > productBalance ?
                                  <Typography variant="caption" color="error" sx={{ fontSize: { xs: '10px', sm: '9px' } }}>
                                    {translations.quantityExceedsInventory}
                                  </Typography>
                                  : EMPTY_STRING
                                }
                                sx={{
                                  width: {
                                    xs: WIDTH_QUANTITY_INPUT_XS,
                                    sm: WIDTH_QUANTITY_INPUT_SM
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: {
                                      xs: '4px',
                                      sm: '8px'
                                    },
                                    fontSize: {
                                      xs: '14px',
                                      sm: '16px'
                                    }
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                                    opacity: 1,
                                    height: SPIN_BUTTON_HEIGHT
                                  }
                                }}
                              />
                              {isLiveDataLoading && (
                                <CircularProgress size={LIVE_DATA_SPINNER_SIZE} sx={{ color: BRAND }} />
                              )}
                            </Box>
                          </TableCell>
                          {selectedProduct && selectedProduct[PRODUCT_FIELDS.QUANTITY] > ZERO && (
                            <TableCell>
                              {product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM] && product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM].length > ZERO ? (
                                <FormControl size={INPUT_SIZE_SMALL} sx={{ minWidth: MIN_WIDTH_SERIAL_SELECT }}>
                                  <Select
                                    value={selectedProduct ? selectedProduct[PRODUCT_FIELDS.SELECTED_SERIAL] || EMPTY_STRING : EMPTY_STRING}
                                    onChange={(e) => handleSerialChange(product[PRODUCT_FIELDS.PART_NAME], e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value={EMPTY_STRING}>
                                      <em>{UI_TEXT.SELECT_BATCH}</em>
                                    </MenuItem>
                                    {product[PRODUCT_FIELDS.PART_BALANCE_SUBFORM].map((batch, index) => (
                                      <MenuItem key={index} value={batch[PRODUCT_FIELDS.SERIAL_NAME] || index}>
                                        {batch[PRODUCT_FIELDS.SERIAL_NAME] || `${UI_TEXT.BATCH_PREFIX} ${index + ONE}`}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2" color="textSecondary" align="center">
                                  {UI_TEXT.NO_VALUE_PLACEHOLDER}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={localSelectedProducts && localSelectedProducts[PRODUCT_FIELDS.QUANTITY] > ZERO ? TABLE_COLSPAN_WITH_SERIAL : TABLE_COLSPAN_WITHOUT_SERIAL} align="center" sx={{ py: 5, border: 'none' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Inventory2OutlinedIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.25)' }} />
                          <Typography color="text.secondary">{translations.noProducts}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '12px 16px' }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, color: BRAND }}
        >
          {translations.cancel}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoadingProducts}
          sx={{
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: BRAND,
            '&:hover': { bgcolor: BRAND_DARK },
          }}
        >
          {translations.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelector;
